import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ViewChild, ElementRef, ChangeDetectorRef, HostListener } from '@angular/core';
import { CommonModule, DatePipe, NgIf } from '@angular/common';
import { CalendarModule } from 'primeng/calendar';
import { FormsModule } from '@angular/forms';
import { ConversationRole, ReminderCount, ReminderHours } from '../../../constants/conversation-roles.constant';
import { SharedModule } from '../../../core/shared/shared.module';
import { AvatarModule } from 'primeng/avatar';
import { TourMetaService } from '../tour-meta.service';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { DropdownModule } from 'primeng/dropdown';
import { AdminMessagingService } from './admin-messaging.service';
import { SendFromType } from '../../../enums/send-from-type.enum';
import { LeadSource } from '../../../enums/lead-source.enum';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TourService } from '../tour.service';
import { TooltipModule } from 'primeng/tooltip';

export interface Tag {
  name: string;
  color: string;
}

export interface TourMeta {
  tourId: string;
  tags: Tag[];
  internalNote?: string;
}

export interface InternalNote {
  text: string;
  createdAt: string;
  latest?: boolean;
}


export interface TourMeta {
  tourId: string;
  tags: Tag[];
  internalNotes: InternalNote[];
}

enum TextType {
  Human = 'human',
  AI = 'ai'
}

@Component({
  selector: 'app-message-conversation',
  standalone: true,
  imports: [NgIf, CalendarModule, FormsModule, DatePipe, CommonModule, SharedModule, AvatarModule, InputTextModule, ButtonModule, InputTextareaModule, DropdownModule, ToastModule, TooltipModule],
  providers: [MessageService],
  templateUrl: './message-conversation.component.html',
  styleUrl: './message-conversation.component.scss'
})
export class MessageConversationComponent implements OnInit, OnChanges {
  @ViewChild('scrollAnchor') scrollAnchor!: ElementRef;
  @ViewChild('tagSelectWrapper') tagSelectWrapper!: ElementRef;
  
  isSending: boolean = false;
  isConfirmingReschedule: boolean = false;
  isSendingMoreTimes: boolean = false;

  @Input() conversations: any[] = [];
  @Input() contactInfo: any;
  @Input() isLoading: any;
  @Input() userType: any;
  @Input() property: any;
  @Input() isSystemConversation: any;

  @Output() close = new EventEmitter<any>();
  @Output() fetchConversation = new EventEmitter<{ conversationFrom: string, conversationTo: string }>();

  tags: Tag[] = [];
  tagColor: string = '#ef4444';

  showTagDropdown = false;
  tagSearch = '';
  allGlobalTags: { name: string; color: string }[] = [];
  filteredGlobalTags: { name: string; color: string }[] = [];
  showCreateForm = false;
  newTagName = '';
  highlightedTag: string | null = null;
  duplicateTagWarning: string = '';

  private readonly TAG_NEUTRAL_COLOR = '#94a3b8';

  internalNotes: InternalNote[] = [];
  internalNote: string = '';
  selectedNote: InternalNote | null = null;
  maxChars: number = 500;
  charCount: number = 0;

  TextType = TextType;
  LeadSource = LeadSource;
  date: Date | undefined;
  activeLink: ConversationRole = ConversationRole.SYSTEM;
  ConversationRole = ConversationRole;
  ReminderCount = ReminderCount;
  ReminderHours = ReminderHours;

  newMessage: string = '';

  constructor(
    private tourMetaService: TourMetaService,
    private adminMessagingService: AdminMessagingService,
    private messageService: MessageService,
    private tourService: TourService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    if (this.contactInfo?._id) {
      this.loadTourMeta();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['conversations'] && this.conversations?.length > 0) {
      this.scrollToBottom();
    }
  }

  private scrollToBottom() {
    this.cdr.detectChanges();
    setTimeout(() => {
      this.scrollAnchor?.nativeElement?.scrollIntoView({ behavior: 'smooth' });
    }, 0);
  }

  isFirstResidentMessage(i: number, conversation: any): boolean {
    return conversation.fromRole === ConversationRole.RESIDENT && !this.conversations.slice(0, i).some(c => c.fromRole === ConversationRole.RESIDENT) && this.contactInfo?.user_name?.trim() !== '';
  }

  loadConversation(conversationFrom: string, conversationTo: string) {
    this.fetchConversation.emit({ conversationFrom, conversationTo });
  }


  closeChat() {
    this.close.emit();
  }

  loadTourMeta() {
    if (!this.contactInfo?._id) return;
    this.tourMetaService.getTourMeta(this.contactInfo._id).subscribe((meta: TourMeta) => {
      this.tags = meta?.tags || [];
      this.internalNotes = meta?.internalNotes || [];
      // Show latest note in textarea
      const latestNote = this.internalNotes.find(n => n.latest);
      this.internalNote = latestNote ? latestNote.text : '';
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.tagSelectWrapper && !this.tagSelectWrapper.nativeElement.contains(event.target as Node)) {
      this.closeTagDropdown();
    }
  }

  toggleTagDropdown() {
    if (this.showTagDropdown) {
      this.closeTagDropdown();
    } else {
      this.showTagDropdown = true;
      this.tagSearch = '';
      this.showCreateForm = false;
      const propertyId = this.contactInfo?.property_id?._id ?? this.contactInfo?.property_id;
      this.tourMetaService.getGlobalTags(propertyId, '').subscribe({
        next: (names) => {
          this.allGlobalTags = names;
          this.filteredGlobalTags = [...names];
        },
        error: () => {
          this.allGlobalTags = [];
          this.filteredGlobalTags = [];
        }
      });
    }
  }

  closeTagDropdown() {
    this.showTagDropdown = false;
    this.showCreateForm = false;
    this.tagSearch = '';
    this.highlightedTag = null;
    this.duplicateTagWarning = '';
  }

  filterGlobalTags() {
    const q = this.tagSearch.toLowerCase();
    this.filteredGlobalTags = this.allGlobalTags.filter(n => n.name.toLowerCase().includes(q));
  }

  isTagSelected(name: string): boolean {
    return this.tags.some(t => t.name.toLowerCase() === name.toLowerCase());
  }

  getTagDotColor(name: string): string {
    // Prefer the color stored on this tour (latest added)
    const tourMatches = this.tags.filter(t => t.name.toLowerCase() === name.toLowerCase());
    if (tourMatches.length > 0) return tourMatches[tourMatches.length - 1].color;
    // Fall back to the global tag's stored color
    const globalTag = this.allGlobalTags.find(t => t.name.toLowerCase() === name.toLowerCase());
    return globalTag?.color ?? this.TAG_NEUTRAL_COLOR;
  }

  toggleTag(name: string) {
    const idx = this.tags.findIndex(t => t.name.toLowerCase() === name.toLowerCase());
    if (idx !== -1) {
      this.removeTag(idx);
    } else {
      const propertyId = this.contactInfo?.property_id?._id ?? this.contactInfo?.property_id;
      const newTag: Tag = { name, color: this.tagColor };
      this.tourMetaService.addTag(this.contactInfo._id, newTag, propertyId).subscribe((res: TourMeta) => {
        this.tags = res.tags || [];
        this.tourMetaService.notifyMetaUpdated({ tourId: this.contactInfo._id, tags: this.tags });
      });
    }
    this.closeTagDropdown();
  }

  openCreateForm() {
    this.newTagName = this.tagSearch;
    this.duplicateTagWarning = '';
    this.highlightedTag = null;
    this.showCreateForm = true;
  }

  submitNewTag() {
    const name = this.newTagName.trim();
    if (!name) return;

    // Already on this tour
    if (this.tags.some(t => t.name.toLowerCase() === name.toLowerCase())) {
      this.closeTagDropdown();
      return;
    }

    // Exists as a global tag — highlight it in the list instead of creating a duplicate
    const matchedGlobal = this.allGlobalTags.find(n => n.name.toLowerCase() === name.toLowerCase());
    if (matchedGlobal) {
      this.showCreateForm = false;
      this.highlightedTag = matchedGlobal.name;
      this.duplicateTagWarning = `"${matchedGlobal.name}" already exists — select it from the list below`;
      setTimeout(() => { this.highlightedTag = null; }, 3000);
      return;
    }

    const propertyId = this.contactInfo?.property_id?._id ?? this.contactInfo?.property_id;
    const newTag: Tag = { name, color: this.tagColor };
    this.tourMetaService.addTag(this.contactInfo._id, newTag, propertyId).subscribe((res: TourMeta) => {
      this.tags = res.tags || [];
      this.tourMetaService.notifyMetaUpdated({ tourId: this.contactInfo._id, tags: this.tags });
      this.closeTagDropdown();
    });
  }

  removeTag(index: number) {
    const tagName = this.tags[index].name;
    this.tourMetaService.removeTag(this.contactInfo._id, tagName).subscribe((res: TourMeta) => {
      this.tags = res.tags || [];
      this.tourMetaService.notifyMetaUpdated({
        tourId: this.contactInfo._id,
        tags: this.tags
      });
    });
  }

  addNote() {
    if (!this.internalNote.trim()) return;

    this.tourMetaService.addNote(this.contactInfo._id, this.internalNote).subscribe(res => {
      this.internalNotes = res.internalNotes || [];

      // ✅ Always set latest note
      this.setLatestNote();

      this.tourMetaService.notifyMetaUpdated({
        tourId: this.contactInfo._id,
        internalNotes: this.internalNotes
      });
    });
  }

  removeNote(note: InternalNote) {
    this.tourMetaService.removeNote(this.contactInfo._id, note.createdAt).subscribe(res => {
      this.internalNotes = res.internalNotes || [];

      // ✅ Always set latest note
      this.setLatestNote();

      this.tourMetaService.notifyMetaUpdated({
        tourId: this.contactInfo._id,
        internalNotes: this.internalNotes
      });

      // Update textarea to latest note if any
      if (this.internalNotes.length > 0) {
        this.internalNote = this.internalNotes[this.internalNotes.length - 1].text;
      } else {
        this.internalNote = '';
      }
    });
  }

  selectNote(note: InternalNote) {
    this.selectedNote = note;
    this.internalNote = note.text;

    // Update latest flags locally (sync with DB later if needed)
    this.internalNotes.forEach(n => (n.latest = false));
    note.latest = true;

    // Optional: save immediately to backend
    this.tourMetaService.setLatestNote(this.contactInfo._id, note.createdAt).subscribe(res => {
      this.internalNotes = res.internalNotes;
    });

    this.tourMetaService.notifyMetaUpdated({
      tourId: this.contactInfo._id,
      internalNotes: this.internalNotes
    });
  }

  setLatestNote() {
    const latest = this.internalNotes.find(n => n.latest);
    if (latest) {
      this.internalNote = latest.text;
      this.selectedNote = latest;
    } else {
      this.internalNote = '';
      this.selectedNote = null;
    }
  }

  updateCharCount() {
    this.charCount = this.internalNote.length;
  }

  sendAdminMessageToVisitor() {
    if (!this.newMessage?.trim()) return;

    const payload = {
      message: this.newMessage,
      fromRole: SendFromType.ADMIN,
      tourId: this.contactInfo?.id
    };

    this.isSending = true;
    this.newMessage = '';

    this.adminMessagingService.sendMessageToVisitor(payload).subscribe({
      next: () => {

      // Push message locally instead of reloading from DB
      this.conversations = [
        ...this.conversations,
        {
          message: payload.message,
          fromRole: SendFromType.ADMIN,
          createdAt: new Date().toISOString(),
          conversationType: `${ConversationRole.ADMIN}-${ConversationRole.VISITOR}`,
          scheduleNumber: 1
        }
      ];

        this.newMessage = '';
        this.isSending = false;
        this.scrollToBottom();
      },
      error: (err) => {
        const errorMessage =
          err?.error?.message || 'Failed to send message';

        console.error('Error message:', errorMessage);  
        console.error('Complete error message:', err); 

        this.isSending = false;
        this.newMessage = ''; 

        this.messageService.add({
          severity: 'error',
          summary: 'Message Not Sent',
          detail: 'Something went wrong while sending your message. Please try again.',
          life: 5000
        });
      }
    });
  }

  confirmReschedule(): void {
    if (!this.contactInfo?._id) return;
    this.isConfirmingReschedule = true;
    this.tourService.confirmReschedule(this.contactInfo._id).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Tour reschedule confirmed. Original resident has been notified.' });
        this.contactInfo.rescheduleRequest = null;
        this.contactInfo.tourStatus = 'rescheduled';
        this.contactInfo.status = 'Pending';
        this.isConfirmingReschedule = false;
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to confirm reschedule. Please try again.' });
        this.isConfirmingReschedule = false;
      }
    });
  }

  sendMoreTimes(): void {
    if (!this.contactInfo?._id) return;
    this.isSendingMoreTimes = true;
    this.tourService.sendMoreTimes(this.contactInfo._id).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Sent', detail: 'Additional availability sent to lead.' });
        this.isSendingMoreTimes = false;
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to send availability. Please try again.' });
        this.isSendingMoreTimes = false;
      }
    });
  }
}